// Ubicación en el repo: src/components/LegalSettings.jsx
// Sub-pestaña "Datos legales" de Configuración — identidad de la clínica que
// aparece en el consentimiento informado y la política de privacidad (razón
// social, RUT, dirección, correo de contacto para derechos ARCOP). Se guarda
// en `clinic_settings` (mismas columnas nuevas de la migración 0040), mismo
// patrón que AppearanceSettings.jsx: solo Admin puede guardar. Este software
// se piensa entregar/colaborar con otras clínicas más adelante (ver
// DECISIONS.md, 22-ago-2026), así que estos datos no pueden quedar fijos en
// el código — consentDocument.js, privacyPolicy.js y la Edge Function
// public-agendamiento-create los leen desde acá.
//
// A propósito NO hay ningún campo de "correo de respuesta" para los correos
// transaccionales (confirmación de hora, recordatorios, invitaciones,
// acceso@) — decisión explícita de Nicolás (22-ago-2026, ver DECISIONS.md):
// esos correos son automáticos y no deben invitar a responder. El correo de
// contacto de acá SOLO aparece dentro de la Política de Privacidad y el
// Consentimiento Informado, para quien los lee y quiere hacer una consulta —
// nunca en el cuerpo de un correo transaccional.
import { useState } from 'react';
import { updateClinicLegalInfo } from '../lib/clinicSettings';

export default function LegalSettings({ settings, isAdmin, actorId, onSaved }) {
  const [form, setForm] = useState({
    razonSocial: settings.razonSocial || '',
    rut: settings.rut || '',
    direccion: settings.direccion || '',
    correoContactoDatos: settings.correoContactoDatos || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setSaved(false);
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      await updateClinicLegalInfo(form, actorId);
      onSaved({ ...settings, ...form });
      setSaved(true);
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los datos legales.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="patients-page">
      <h1>Datos legales</h1>
      <p className="text-muted" style={{ marginBottom: 18 }}>
        Estos datos identifican a la clínica en el documento de consentimiento informado (impreso desde la
        ficha de paciente y el que se genera automáticamente en el auto-agendamiento online). No aparecen en
        ninguna otra parte del sistema.
      </p>

      {!isAdmin && <p className="text-muted">Solo un Admin puede editar los datos legales.</p>}

      <div style={{ opacity: isAdmin ? 1 : 0.6, pointerEvents: isAdmin ? 'auto' : 'none' }}>
        <div className="form-row">
          <label className="field">
            <span>Razón social</span>
            <input
              type="text"
              value={form.razonSocial}
              onChange={(e) => set('razonSocial', e.target.value)}
              placeholder="Ej: Óptica Comunal SpA"
            />
          </label>
          <label className="field">
            <span>RUT</span>
            <input
              type="text"
              value={form.rut}
              onChange={(e) => set('rut', e.target.value)}
              placeholder="Ej: 76.000.000-0"
            />
          </label>
        </div>

        <label className="field">
          <span>Dirección</span>
          <input
            type="text"
            value={form.direccion}
            onChange={(e) => set('direccion', e.target.value)}
            placeholder="Dirección de la casa matriz o sucursal principal"
          />
        </label>

        <label className="field">
          <span>Correo de contacto (consultas de privacidad)</span>
          <input
            type="email"
            value={form.correoContactoDatos}
            onChange={(e) => set('correoContactoDatos', e.target.value)}
            placeholder="ej: consulta@tudominio.cl"
          />
          <span className="field-hint">
            Aparece solo dentro de la Política de Privacidad y el Consentimiento Informado, para el paciente
            que los lee y quiere hacer una consulta o ejercer sus derechos sobre sus datos (acceso,
            rectificación, eliminación). No aparece en los correos de confirmación de hora ni en ningún otro
            correo automático — esos son solo informativos, no invitan a responder. Tiene que ser un correo
            que efectivamente revisen (o que reenvíe a uno que sí) — no alcanza con una dirección que solo
            envía, como pasa hoy con agendamiento@/acceso@.
          </span>
        </label>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {saved && !error && <p className="text-muted">Guardado.</p>}

      {isAdmin && (
        <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="button-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar datos legales'}
          </button>
        </div>
      )}
    </div>
  );
}
